import type { LocalConfig, LocalPlayerState, LocalZoneRect } from "./types";
import { LOCAL_COOP_MODES } from "./types";
import { computeLocalZones, zoneIndexAt } from "./zones";
import { hideLocalScreens, isLocalScreenVisible } from "./lobby-ui";
import { tickHeat, getHeatCursorColor } from "../heat-mode";
import { withExitPlayTransition } from "../play-transition";

export type LocalGameHooks = {
    getCanvasSize: () => { w: number; h: number };
    getCursorRadius: () => number;
    getCursorEdgeInset: () => number;
    getLayoutScale: () => number;
    getWasdSensitivity: () => number;
    isPlaying: () => boolean;
    isLocalResultVisible: () => boolean;
    isDeathPending: () => boolean;
    isResumeCountdownActive: () => boolean;
    isGamePaused: () => boolean;
    setMouse: (x: number, y: number) => void;
    getMouse: () => { x: number; y: number };
    setCollisionPrevMouse: (x: number, y: number) => void;
    getCollisionPrevMouse: () => { x: number; y: number };
    convertClientDelta: (dx: number, dy: number) => { dx: number; dy: number };
    clampLocalCursor: (x: number, y: number) => { x: number; y: number };
    beginDeath: (message?: string) => void;
    showLocalResult: (message: string) => void;
    showLocalWinScreen: (title: string, message?: string) => void;
    hideLocalResult: () => void;
    getTouchJoystickSensitivity: () => number;
    openLocalSetup: () => void;
    openModes: () => void;
    stopPlaying: () => void;
    drawCursorAt: (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => void;
    drawPlayerName: (ctx: CanvasRenderingContext2D, x: number, y: number, name: string, color: string) => void;
    clearLocalMatchVisuals: () => void;
    drawHeatBarInZone: (
        ctx: CanvasRenderingContext2D,
        playerIndex: number,
        playerCount: number,
        level: number,
        cw: number,
        ch: number,
        layoutScale: number,
        cursorY: number,
    ) => void;
    checkObstacleHit: (px: number, py: number, ppx: number, ppy: number) => boolean;
    checkSnakeBorder: (x: number, y: number, cr: number) => boolean;
    checkSnakeSelfTrail: (x: number, y: number, cr: number, ppx: number, ppy: number, playerIndex: number) => boolean;
    checkSnakeOtherTrails: (x: number, y: number, cr: number, ppx: number, ppy: number, playerIndex: number) => boolean;
    appendSnakeTrail: (playerIndex: number, x: number, y: number) => void;
    drawSnakeTrails: (ctx: CanvasRenderingContext2D) => void;
    drawSnakeBorder: (ctx: CanvasRenderingContext2D) => void;
    checkHighwayDeath: (x: number, y: number, cr: number) => string | null;
    checkSpeedrunDeath: (
        x: number,
        y: number,
        ppx: number,
        ppy: number,
        playerIndex: number,
    ) => { deathMsg: string | null; x: number; y: number };
    onSpeedrunCheckpoint: (playerIndex: number) => void;
    /** Death FX + freeze sim time; return spawn px for all cursors after delay. */
    beginLocalSpeedrunLaserDeathFx: () => void;
    finishLocalSpeedrunLaserRespawn: () => { x: number; y: number };
    checkColorRushHit: (x: number, y: number, ppx: number, ppy: number) => "wrong" | "ok" | null;
    checkCounterHit: (x: number, y: number, ppx: number, ppy: number) => "wrong" | "ok" | null;
    getColorRushCursorColor: () => string;
    getAllCursorPositions: () => Array<{ x: number; y: number }>;
};

let hooks: LocalGameHooks | null = null;
let active = false;
let config: LocalConfig | null = null;
let players: LocalPlayerState[] = [];
let zones: LocalZoneRect[] = [];
let divisionAnimStartAt = 0;
const LOCAL_DIVISION_FADE_IN_MS = 1000;
const LOCAL_DIVISION_HOLD_MS = 1000;
const LOCAL_DIVISION_FADE_OUT_MS = 1000;
const LOCAL_DIVISION_PEAK_OPACITY = 0.45;

type SteerSession = {
    playerIndex: number;
    lastClientX: number;
    lastClientY: number;
    liveClientX: number;
    liveClientY: number;
    pointerId: number;
};

const steerSessions = new Map<number, SteerSession>();
const zoneDpads = new Map<number, { w: boolean; a: boolean; s: boolean; d: boolean }>();
let touchZonesContainer: HTMLElement | null = null;
let touchZonesMounted = false;
let matchEnded = false;
let winnerName = "";
const LOCAL_END_FREEZE_MS = 1500;
/** Laser death in LOCAL Speedrun: freeze briefly, then respawn (no game over). */
const LOCAL_SPEEDRUN_RESPAWN_MS = 1000;
type LocalEndKind = "ffa_win" | "coop_win" | "coop_lose" | "game_over";
type LocalEndPending = { kind: LocalEndKind; title: string; message: string };
let localEndPending: LocalEndPending | null = null;
let localEndFreezeUntil = 0;
let localResultShown = false;
let speedrunRespawnPending = false;
let speedrunRespawnUntil = 0;

type ZoneJoystick = {
    playerIndex: number;
    pointerId: number;
    baseX: number;
    baseY: number;
    curX: number;
    curY: number;
    maxR: number;
    wrap: HTMLElement;
    knob: HTMLElement;
};

const zoneJoysticks = new Map<number, ZoneJoystick>();

export function isLocalActive(): boolean {
    return active;
}

export function getLocalConfig(): LocalConfig | null {
    return config;
}

export function isLocalCoopMode(): boolean {
    return !!config && LOCAL_COOP_MODES.includes(config.mode);
}

export function shouldHideGlobalTouchPad(): boolean {
    return active;
}

export function getLocalAllCursorPositions(): Array<{ x: number; y: number }> {
    return players.filter((p) => p.alive).map((p) => ({ x: p.x, y: p.y }));
}

export function initLocalGameRuntime(h: LocalGameHooks): void {
    hooks = h;
}

export function startLocalSession(
    cfg: LocalConfig,
    cw: number,
    ch: number,
    sharedSpawn?: { x: number; y: number } | null,
): void {
    config = cfg;
    active = true;
    matchEnded = false;
    winnerName = "";
    localEndPending = null;
    localEndFreezeUntil = 0;
    localResultShown = false;
    speedrunRespawnPending = false;
    speedrunRespawnUntil = 0;
    divisionAnimStartAt = 0;
    zones = computeLocalZones(cw, ch, cfg.playerCount);
    steerSessions.clear();
    zoneDpads.clear();
    zoneJoysticks.clear();
    players = [];
    touchZonesMounted = false;
    const useSharedSpawn = !!sharedSpawn && cfg.mode === "speedrun";
    for (let i = 0; i < cfg.playerCount; i++) {
        const z = zones[i]!;
        const slot = cfg.players[i]!;
        const rawX = useSharedSpawn ? sharedSpawn!.x : z.cx;
        const rawY = useSharedSpawn ? sharedSpawn!.y : z.cy;
        const pos = hooks?.clampLocalCursor(rawX, rawY) ?? { x: rawX, y: rawY };
        players.push({
            index: i,
            name: slot.name,
            color: slot.color,
            x: pos.x,
            y: pos.y,
            prevX: pos.x,
            prevY: pos.y,
            alive: true,
            zone: z,
            heatLevel: 0.5,
            wasdKeys: { w: false, a: false, s: false, d: false },
        });
        zoneDpads.set(i, { w: false, a: false, s: false, d: false });
    }
    syncPrimaryMouse();
    hooks?.hideLocalResult();
}

export function stopLocalSession(): void {
    active = false;
    config = null;
    players = [];
    zones = [];
    steerSessions.clear();
    zoneDpads.clear();
    zoneJoysticks.clear();
    matchEnded = false;
    winnerName = "";
    localEndPending = null;
    localEndFreezeUntil = 0;
    localResultShown = false;
    speedrunRespawnPending = false;
    speedrunRespawnUntil = 0;
    divisionAnimStartAt = 0;
    touchZonesMounted = false;
    if (touchZonesContainer) touchZonesContainer.replaceChildren();
    touchZonesContainer = null;
}

export function isLocalSpeedrunRespawnPending(): boolean {
    return active && speedrunRespawnPending;
}

export function isLocalMatchUpdatesFrozen(): boolean {
    if (!active) return false;
    if (localResultShown) return true;
    if (localEndFreezeUntil > 0 && performance.now() < localEndFreezeUntil) return true;
    return false;
}

export function isLocalResultShown(): boolean {
    return localResultShown;
}

export function scheduleLocalMatchEnd(opts: {
    kind: LocalEndKind;
    winnerName?: string;
    title?: string;
    message?: string;
}): void {
    if (!hooks || matchEnded) return;
    matchEnded = true;
    const name = opts.winnerName?.trim() || "";
    winnerName = name;
    let title = opts.title ?? "";
    let message = opts.message ?? "";
    if (opts.kind === "ffa_win") {
        title = `${name || "PLAYER"} WON`;
        message = "";
    } else if (opts.kind === "coop_win") {
        title = title || "VICTORY";
    } else if (opts.kind === "coop_lose") {
        title = title || "DEFEAT";
        message = message || "Everyone lost!";
    } else if (opts.kind === "game_over") {
        title = title || "GAME OVER";
    }
    localEndPending = { kind: opts.kind, title, message };
    localEndFreezeUntil = performance.now() + LOCAL_END_FREEZE_MS;
    localResultShown = false;
    hooks.clearLocalMatchVisuals();
}

export function tickLocalEndSequence(now: number): void {
    if (!active || !hooks || !localEndPending) return;
    if (localResultShown) return;
    if (now < localEndFreezeUntil) return;
    const pending = localEndPending;
    localEndPending = null;
    localEndFreezeUntil = 0;
    localResultShown = true;
    hideLocalTouchInput();
    hooks.showLocalWinScreen(pending.title, pending.message);
}

function alivePlayers(): LocalPlayerState[] {
    return players.filter((p) => p.alive);
}

function syncPrimaryMouse(): void {
    const p = alivePlayers()[0] ?? players[0];
    if (!p || !hooks) return;
    hooks.setMouse(p.x, p.y);
    hooks.setCollisionPrevMouse(p.prevX, p.prevY);
}

export function onLocalCountdownComplete(): void {
    if (!active) return;
    divisionAnimStartAt = performance.now();
}

function divisionAnimElapsed(now: number): number {
    if (divisionAnimStartAt <= 0) return -1;
    return now - divisionAnimStartAt;
}

function isIntroOverlayActive(now: number): boolean {
    const elapsed = divisionAnimElapsed(now);
    if (elapsed < 0) return false;
    return elapsed < LOCAL_DIVISION_FADE_IN_MS + LOCAL_DIVISION_HOLD_MS + LOCAL_DIVISION_FADE_OUT_MS;
}

function divisionOverlayOpacity(now: number): number {
    if (hooks?.isResumeCountdownActive()) return 0;
    const elapsed = divisionAnimElapsed(now);
    if (elapsed < 0) return 0;
    const peak = LOCAL_DIVISION_PEAK_OPACITY;
    const fadeInEnd = LOCAL_DIVISION_FADE_IN_MS;
    const holdEnd = fadeInEnd + LOCAL_DIVISION_HOLD_MS;
    const animEnd = holdEnd + LOCAL_DIVISION_FADE_OUT_MS;
    if (elapsed < fadeInEnd) return peak * (elapsed / fadeInEnd);
    if (elapsed < holdEnd) return peak;
    if (elapsed < animEnd) {
        const t = (elapsed - holdEnd) / LOCAL_DIVISION_FADE_OUT_MS;
        return peak * (1 - t);
    }
    if (config?.showDivisions) return 0.12;
    return 0;
}

export function isLocalPreRoundFrozen(): boolean {
    return active && !!hooks?.isResumeCountdownActive();
}

export function isLocalGameplayInputBlocked(): boolean {
    if (!active) return false;
    return (
        matchEnded ||
        localResultShown ||
        speedrunRespawnPending ||
        !!hooks?.isResumeCountdownActive()
    );
}

function hideLocalTouchInput(): void {
    steerSessions.clear();
    for (const joy of zoneJoysticks.values()) joy.wrap.remove();
    zoneJoysticks.clear();
    if (touchZonesContainer) {
        touchZonesContainer.classList.add("hidden");
        touchZonesContainer.replaceChildren();
    }
    touchZonesMounted = false;
}

function tickLocalSpeedrunRespawn(now: number): void {
    if (!active || !hooks || !config || !speedrunRespawnPending) return;
    if (now < speedrunRespawnUntil) return;
    speedrunRespawnPending = false;
    speedrunRespawnUntil = 0;
    const spawn = hooks.finishLocalSpeedrunLaserRespawn();
    for (const p of players) {
        p.alive = true;
        p.x = spawn.x;
        p.y = spawn.y;
        p.prevX = spawn.x;
        p.prevY = spawn.y;
    }
    syncPrimaryMouse();
}

function beginLocalSpeedrunLaserDeath(): void {
    if (!hooks || !config || matchEnded || speedrunRespawnPending) return;
    speedrunRespawnPending = true;
    speedrunRespawnUntil = performance.now() + LOCAL_SPEEDRUN_RESPAWN_MS;
    for (const pl of players) pl.alive = true;
    hooks.beginLocalSpeedrunLaserDeathFx();
}

export function tickLocalMultiplayer(dt: number): void {
    if (!active || !hooks || !config) return;
    const now = performance.now();
    tickLocalEndSequence(now);
    tickLocalSpeedrunRespawn(now);
    if (isLocalMatchUpdatesFrozen()) return;
    if (matchEnded || hooks.isDeathPending() || hooks.isGamePaused()) return;
    if (isLocalGameplayInputBlocked()) return;

    const fs = Math.min(3, dt * 60);
    const ls = hooks.getLayoutScale();

    for (const p of players) {
        if (!p.alive) continue;
        p.prevX = p.x;
        p.prevY = p.y;

        if (config.mode === "wasd") {
            const keys = zoneDpads.get(p.index) ?? p.wasdKeys;
            const move = hooks.getWasdSensitivity() * ls * fs;
            if (keys.w) p.y -= move;
            if (keys.s) p.y += move;
            if (keys.a) p.x -= move;
            if (keys.d) p.x += move;
        }

        const clamped = hooks.clampLocalCursor(p.x, p.y);
        p.x = clamped.x;
        p.y = clamped.y;
    }

    if (config.useJoystick && config.mode !== "wasd") {
        const sens = hooks.getTouchJoystickSensitivity();
        for (const joy of zoneJoysticks.values()) {
            const p = players[joy.playerIndex];
            if (!p || !p.alive) continue;
            const dx = joy.curX - joy.baseX;
            const dy = joy.curY - joy.baseY;
            const m = Math.hypot(dx, dy);
            const dead = 6;
            const capR = joy.maxR;
            if (m < dead) continue;
            const ux = dx / m;
            const uy = dy / m;
            const capped = Math.min(m, capR);
            const strength = (capped - dead) / Math.max(1e-6, capR - dead);
            const sp = 640 * (sens / 4) * strength;
            let nx = p.x + ux * sp * dt;
            let ny = p.y + (config.mode === "highway" ? -uy : uy) * sp * dt;
            const next = hooks.clampLocalCursor(nx, ny);
            p.x = next.x;
            p.y = next.y;
        }
    } else if (config.mode !== "wasd") {
        for (const sess of steerSessions.values()) {
            const p = players[sess.playerIndex];
            if (!p || !p.alive) continue;
            const rawDx = sess.liveClientX - sess.lastClientX;
            const rawDy = sess.liveClientY - sess.lastClientY;
            if (rawDx === 0 && rawDy === 0) continue;
            const { dx, dy } = hooks.convertClientDelta(rawDx, rawDy);
            let nx = p.x + dx;
            let ny = p.y + (config.mode === "highway" ? -dy : dy);
            const next = hooks.clampLocalCursor(nx, ny);
            p.x = next.x;
            p.y = next.y;
            sess.lastClientX = sess.liveClientX;
            sess.lastClientY = sess.liveClientY;
        }
    }

    syncPrimaryMouse();

    if (config.mode === "snake") {
        for (const p of players) {
            if (!p.alive) continue;
            hooks.appendSnakeTrail(p.index, p.x, p.y);
        }
    }

    if (config.mode === "heat") {
        for (const p of players) {
            if (!p.alive) continue;
            const death = tickHeat(p.prevX, p.prevY, p.x, p.y, dt, ls);
            if (death) {
                p.heatLevel = death === "hot" ? 1 : 0;
                handlePlayerDeath(p.index, death === "hot" ? "Excess heat!" : "Excess cold!");
                return;
            }
            p.heatLevel += 0;
        }
    }
}

export function handleLocalCollisions(): boolean {
    if (!active || !hooks || !config || matchEnded) return false;
    if (isLocalGameplayInputBlocked()) return true;

    const cr = hooks.getCursorRadius();

    for (const p of players) {
        if (!p.alive) continue;

        if (config.mode === "snake") {
            if (hooks.checkSnakeBorder(p.x, p.y, cr)) {
                handlePlayerDeath(p.index, "Hit the wall.");
                return true;
            }
            if (hooks.checkSnakeSelfTrail(p.x, p.y, cr, p.prevX, p.prevY, p.index)) {
                handlePlayerDeath(p.index, "Hit your trail.");
                return true;
            }
            if (hooks.checkSnakeOtherTrails(p.x, p.y, cr, p.prevX, p.prevY, p.index)) {
                handlePlayerDeath(p.index, "Hit a trail.");
                return true;
            }
            continue;
        }

        if (config.mode === "highway") {
            const msg = hooks.checkHighwayDeath(p.x, p.y, cr);
            if (msg) {
                handlePlayerDeath(p.index, msg);
                return true;
            }
            continue;
        }

        if (config.mode === "speedrun") {
            const res = hooks.checkSpeedrunDeath(p.x, p.y, p.prevX, p.prevY, p.index);
            p.x = res.x;
            p.y = res.y;
            p.prevX = res.x;
            p.prevY = res.y;
            if (res.deathMsg) {
                handlePlayerDeath(p.index, res.deathMsg);
                return true;
            }
            continue;
        }

        if (config.mode === "colorrush") {
            const hit = hooks.checkColorRushHit(p.x, p.y, p.prevX, p.prevY);
            if (hit === "wrong") {
                handlePlayerDeath(p.index, "Wrong color!");
                return true;
            }
            continue;
        }

        if (config.mode === "counter") {
            const hit = hooks.checkCounterHit(p.x, p.y, p.prevX, p.prevY);
            if (hit === "wrong") {
                handlePlayerDeath(p.index, "Wrong ball!");
                return true;
            }
            continue;
        }

        if (
            config.mode === "normal" ||
            config.mode === "wasd" ||
            config.mode === "heat" ||
            config.mode === "sandbox"
        ) {
            if (hooks.checkObstacleHit(p.x, p.y, p.prevX, p.prevY)) {
                handlePlayerDeath(p.index);
                return true;
            }
        }
    }
    return true;
}

function handlePlayerDeath(playerIndex: number, message = ""): void {
    if (!hooks || !config || matchEnded || speedrunRespawnPending) return;
    const p = players[playerIndex];
    if (!p || !p.alive) return;

    if (config.mode === "speedrun") {
        beginLocalSpeedrunLaserDeath();
        return;
    }

    if (isLocalCoopMode()) {
        for (const pl of players) pl.alive = false;
        scheduleLocalMatchEnd({
            kind: "coop_lose",
            message: message || "Everyone lost!",
        });
        return;
    }

    p.alive = false;
    const remaining = alivePlayers();
    if (remaining.length === 0) {
        scheduleLocalMatchEnd({ kind: "game_over", message: message || "Game over!" });
        return;
    }
    if (remaining.length === 1) {
        scheduleLocalMatchEnd({ kind: "ffa_win", winnerName: remaining[0]!.name });
        return;
    }
}

export function onLocalSpeedrunCheckpoint(playerIndex: number): void {
    if (!active || !config || config.mode !== "speedrun" || !isLocalCoopMode()) return;
    hooks?.onSpeedrunCheckpoint(playerIndex);
}

export function renderLocalOverlay(ctx: CanvasRenderingContext2D, cw: number, ch: number, now: number): void {
    if (!active || !config) return;
    if (matchEnded || localResultShown) return;

    const lineOpacity = divisionOverlayOpacity(now);
    const showIntroLabels = isIntroOverlayActive(now);

    if (lineOpacity > 0) {
        drawDivisionLines(ctx, cw, ch, lineOpacity);
    }

    if (showIntroLabels) {
        for (let i = 0; i < zones.length; i++) {
            const p = players[i];
            if (p) hooks?.drawPlayerName(ctx, zones[i]!.cx, zones[i]!.cy, p.name, p.color);
        }
    }

    if (config.mode === "heat") {
        const ls = hooks?.getLayoutScale() ?? 1;
        for (const p of players) {
            if (!p.alive) continue;
            hooks?.drawHeatBarInZone(ctx, p.index, players.length, p.heatLevel, cw, ch, ls, p.y);
        }
    }

    if (config.mode === "snake") {
        hooks?.drawSnakeBorder(ctx);
        hooks?.drawSnakeTrails(ctx);
    }

    for (const p of players) {
        if (!p.alive) continue;
        const color =
            config.mode === "heat"
                ? getHeatCursorColor(p.heatLevel)
                : config.mode === "colorrush"
                  ? hooks?.getColorRushCursorColor() ?? p.color
                  : p.color;
        hooks?.drawCursorAt(ctx, p.x, p.y, color);
        if (config.showUsernames && !showIntroLabels) {
            hooks?.drawPlayerName(ctx, p.x, p.y - (hooks?.getCursorRadius() ?? 8) - 10, p.name, p.color);
        }
    }
}

function drawDivisionLines(ctx: CanvasRenderingContext2D, cw: number, ch: number, opacity: number): void {
    ctx.save();
    ctx.strokeStyle = `rgba(255,255,255,${opacity})`;
    ctx.lineWidth = 2;
    if (zones.length === 2) {
        ctx.beginPath();
        ctx.moveTo(cw / 2, 0);
        ctx.lineTo(cw / 2, ch);
        ctx.stroke();
    } else if (zones.length === 3) {
        ctx.beginPath();
        ctx.moveTo(cw / 3, 0);
        ctx.lineTo(cw / 3, ch);
        ctx.moveTo((cw / 3) * 2, 0);
        ctx.lineTo((cw / 3) * 2, ch);
        ctx.stroke();
    } else if (zones.length === 4) {
        ctx.beginPath();
        ctx.moveTo(cw / 2, 0);
        ctx.lineTo(cw / 2, ch);
        ctx.moveTo(0, ch / 2);
        ctx.lineTo(cw, ch / 2);
        ctx.stroke();
    }
    ctx.restore();
}

function applyZoneElLayout(zoneEl: HTMLElement, z: LocalZoneRect, cw: number, ch: number): void {
    zoneEl.style.left = `${(z.x / cw) * 100}%`;
    zoneEl.style.top = `${(z.y / ch) * 100}%`;
    zoneEl.style.width = `${(z.w / cw) * 100}%`;
    zoneEl.style.height = `${(z.h / ch) * 100}%`;
}

export function relayoutLocalTouchZones(cw: number, ch: number): void {
    if (!active || !config) return;
    zones = computeLocalZones(cw, ch, config.playerCount);
    for (let i = 0; i < players.length; i++) {
        players[i]!.zone = zones[i]!;
    }
    if (!touchZonesContainer) return;
    const els = [...touchZonesContainer.querySelectorAll<HTMLElement>(".local-touch-zone")];
    for (let i = 0; i < zones.length; i++) {
        const el = els[i];
        const z = zones[i];
        if (el && z) applyZoneElLayout(el, z, cw, ch);
    }
}

export function mountLocalTouchZones(container: HTMLElement): void {
    touchZonesContainer = container;
    if (!active || !config) {
        container.replaceChildren();
        touchZonesMounted = false;
        return;
    }
    if (touchZonesMounted) {
        relayoutLocalTouchZones(hooks?.getCanvasSize().w ?? 1, hooks?.getCanvasSize().h ?? 1);
        return;
    }

    container.replaceChildren();
    const { w: cw, h: ch } = hooks?.getCanvasSize() ?? { w: 1, h: 1 };

    for (let i = 0; i < players.length; i++) {
        const z = zones[i]!;
        const zoneEl = document.createElement("div");
        zoneEl.className = "local-touch-zone";
        zoneEl.dataset.localZone = String(i);
        applyZoneElLayout(zoneEl, z, cw, ch);

        if (config.mode === "wasd") {
            zoneEl.innerHTML = buildZoneDpadHtml(i);
            wireZoneDpad(zoneEl, i);
        }

        if (config.useJoystick) {
            wireZoneJoystick(zoneEl, i);
        } else {
            wireZoneSteer(zoneEl, i);
        }
        container.appendChild(zoneEl);
    }
    touchZonesMounted = true;
}

function updateZoneJoystickVisual(joy: ZoneJoystick): void {
    let dx = joy.curX - joy.baseX;
    let dy = joy.curY - joy.baseY;
    const m = Math.hypot(dx, dy);
    if (m > joy.maxR && m > 1e-6) {
        const s = joy.maxR / m;
        dx *= s;
        dy *= s;
    }
    joy.knob.style.transform = `translate(${dx}px, ${dy}px)`;
}

function removeZoneJoystick(pointerId: number): void {
    const joy = zoneJoysticks.get(pointerId);
    if (!joy) return;
    joy.wrap.remove();
    zoneJoysticks.delete(pointerId);
}

function wireZoneJoystick(zoneEl: HTMLElement, playerIndex: number): void {
    if (!config || config.mode === "wasd") return;

    zoneEl.addEventListener(
        "pointerdown",
        (e) => {
            if (!active || isLocalGameplayInputBlocked()) return;
            if (zoneJoysticks.has(e.pointerId)) return;
            const p = players[playerIndex];
            if (!p || !p.alive) return;

            const wrap = document.createElement("div");
            wrap.className = "local-zone-joystick";
            wrap.innerHTML =
                '<div class="touch-float-joyst-ring"></div><div class="touch-float-joyst-knob"></div>';
            const knob = wrap.querySelector<HTMLElement>(".touch-float-joyst-knob");
            if (!knob) return;

            const rect = zoneEl.getBoundingClientRect();
            const localX = e.clientX - rect.left;
            const localY = e.clientY - rect.top;
            wrap.style.left = `${localX}px`;
            wrap.style.top = `${localY}px`;
            zoneEl.appendChild(wrap);

            const joy: ZoneJoystick = {
                playerIndex,
                pointerId: e.pointerId,
                baseX: e.clientX,
                baseY: e.clientY,
                curX: e.clientX,
                curY: e.clientY,
                maxR: 72,
                wrap,
                knob,
            };
            zoneJoysticks.set(e.pointerId, joy);
            updateZoneJoystickVisual(joy);

            try {
                zoneEl.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
            e.preventDefault();
            e.stopPropagation();
        },
        { passive: false },
    );

    zoneEl.addEventListener(
        "pointermove",
        (e) => {
            const joy = zoneJoysticks.get(e.pointerId);
            if (!joy || joy.playerIndex !== playerIndex) return;
            joy.curX = e.clientX;
            joy.curY = e.clientY;
            updateZoneJoystickVisual(joy);
            e.preventDefault();
            e.stopPropagation();
        },
        { passive: false },
    );

    const endJoy = (e: PointerEvent) => {
        if (zoneJoysticks.get(e.pointerId)?.playerIndex === playerIndex) {
            removeZoneJoystick(e.pointerId);
        }
    };
    zoneEl.addEventListener("pointerup", endJoy);
    zoneEl.addEventListener("pointercancel", endJoy);
}

function buildZoneDpadHtml(playerIndex: number): string {
    return `
        <div class="local-zone-dpad" data-local-dpad="${playerIndex}">
            <button type="button" class="touch-dpad-btn touch-dpad-up" data-dpad="up">▲</button>
            <button type="button" class="touch-dpad-btn touch-dpad-left" data-dpad="left">◀</button>
            <button type="button" class="touch-dpad-btn touch-dpad-right" data-dpad="right">▶</button>
            <button type="button" class="touch-dpad-btn touch-dpad-down" data-dpad="down">▼</button>
        </div>
    `;
}

function wireZoneDpad(zoneEl: HTMLElement, playerIndex: number): void {
    const dpad = zoneEl.querySelector<HTMLElement>(".local-zone-dpad");
    if (!dpad) return;
    const setKey = (key: "w" | "a" | "s" | "d", on: boolean) => {
        const keys = zoneDpads.get(playerIndex) ?? { w: false, a: false, s: false, d: false };
        keys[key] = on;
        zoneDpads.set(playerIndex, keys);
        const p = players[playerIndex];
        if (p) p.wasdKeys = keys;
    };
    const map: Record<string, "w" | "a" | "s" | "d"> = {
        up: "w",
        down: "s",
        left: "a",
        right: "d",
    };
    dpad.querySelectorAll<HTMLButtonElement>("[data-dpad]").forEach((btn) => {
        const dir = btn.getAttribute("data-dpad") ?? "";
        const key = map[dir];
        if (!key) return;
        const down = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            setKey(key, true);
        };
        const up = (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            setKey(key, false);
        };
        btn.addEventListener("pointerdown", down);
        btn.addEventListener("pointerup", up);
        btn.addEventListener("pointercancel", up);
        btn.addEventListener("pointerleave", up);
    });
}

function wireZoneSteer(zoneEl: HTMLElement, playerIndex: number): void {
    if (!config || config.mode === "wasd") return;

    zoneEl.addEventListener(
        "pointerdown",
        (e) => {
            if (!active || isLocalGameplayInputBlocked()) return;
            const rect = zoneEl.getBoundingClientRect();
            const p = players[playerIndex];
            if (!p || !p.alive) return;
            steerSessions.set(e.pointerId, {
                playerIndex,
                lastClientX: e.clientX,
                lastClientY: e.clientY,
                liveClientX: e.clientX,
                liveClientY: e.clientY,
                pointerId: e.pointerId,
            });
            try {
                zoneEl.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
            e.preventDefault();
            e.stopPropagation();
        },
        { passive: false },
    );

    zoneEl.addEventListener(
        "pointermove",
        (e) => {
            const sess = steerSessions.get(e.pointerId);
            if (!sess || sess.playerIndex !== playerIndex) return;
            if (!players[playerIndex]?.alive) return;
            sess.liveClientX = e.clientX;
            sess.liveClientY = e.clientY;
            e.preventDefault();
            e.stopPropagation();
        },
        { passive: false },
    );

    const endSteer = (e: PointerEvent) => {
        steerSessions.delete(e.pointerId);
    };
    zoneEl.addEventListener("pointerup", endSteer);
    zoneEl.addEventListener("pointercancel", endSteer);
}

export function refreshLocalTouchZoneLayout(container: HTMLElement): void {
    if (!active) {
        container.replaceChildren();
        return;
    }
    mountLocalTouchZones(container);
}

export function handleLocalReturn(): boolean {
    if (!hooks) return false;

    if (isLocalScreenVisible()) {
        withExitPlayTransition(() => {
            hideLocalScreens();
            hooks?.openModes();
        });
        return true;
    }

    if (hooks.isLocalResultVisible()) {
        withExitPlayTransition(() => {
            hooks?.hideLocalResult();
            hooks?.stopPlaying();
            hooks?.openLocalSetup();
        });
        return true;
    }

    if (localResultShown) {
        withExitPlayTransition(() => {
            hooks?.hideLocalResult();
            hooks?.stopPlaying();
            hooks?.openLocalSetup();
        });
        return true;
    }

    if (!active || !hooks.isPlaying()) return false;

    withExitPlayTransition(() => {
        hooks?.hideLocalResult();
        hooks?.stopPlaying();
        hooks?.openLocalSetup();
    });
    return true;
}

export function pointInAnyLocalZone(px: number, py: number): boolean {
    return zoneIndexAt(px, py, zones) >= 0;
}

export function localSpawnClearanceCenters(): Array<{ x: number; y: number }> {
    return players.map((p) => ({ x: p.x, y: p.y }));
}
